import { AUTH_KEYS, getAuthToken } from '@/shared/utils/authStorage';
import type { AxiosResponse } from 'axios';
import { instance2 } from './base';

export interface IServiceCenterPhone {
    prefix: string;
    no: string;
}

export interface IServiceCenterCoordinate {
    latitude: number;
    longitude: number;
}

export interface IServiceCenter {
    id: string;
    name: string | null;
    address: string | null;
    phone: IServiceCenterPhone;
    city: string | null;
    state: string | null;
    email: string | null;
    website: string | null;
    description: string | null;
    coordinate: IServiceCenterCoordinate;
}

export interface IServiceType {
    id: string;
    name: string;
}

export interface IProductInfo {
    serial_number: string;
    product: {
        name: string;
        images_url: string | null;
    };
}

export interface IAppointmentCancelReason {
    remark: string | null;
    reason: {
        value: string;
        label: string;
    };
    cancelled_by: {
        name: string;
        value: string;
    };
}

export interface IAppointment {
    id: string;
    date: string;
    status: {
        label: string;
        value: string;
    };
    start_time: string;
    product_info: IProductInfo;
    remarks: string | null;
    service_type: IServiceType | null;
    service_center: IServiceCenter;
    cancel_reason: IAppointmentCancelReason | null;
}

export interface IAppointmentListResponse {
    data: IAppointment[];
    meta?: {
        current_page: number;
        from: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
        previous: boolean;
        next: boolean;
        next_link: string | null;
    };
    success: boolean;
    __message: string | null;
}

export const getBookingAppointments = (
    type: string | null = null,
    page: number | null = null,
    limit: number | null = null,
    search: string | null = null,
    signal: AbortSignal | null = null
): Promise<AxiosResponse<IAppointmentListResponse>> =>
    instance2
        .get<IAppointmentListResponse>('/api/v1/appointments/list', {
            signal: signal || undefined,
            params: {
                type: type,
                page: page,
                limit: limit,
                name: search,
                customer_id: getAuthToken(AUTH_KEYS.CUSTOMER_ID),
            },
        });

export const cancelAppointment = (
    id: string | null = null,
    body: Record<string, unknown> = {}
): Promise<AxiosResponse<unknown>> =>
    instance2
        .post<unknown>('/api/v1/appointments/cancel/' + id, body);

export const getServiceTypes = (): Promise<AxiosResponse<unknown>> =>
    instance2
        .get<unknown>('/api/v1/appointments/types', {
            params: {},
        });

export const getServiceCenters = (): Promise<AxiosResponse<unknown>> =>
    instance2
        .get<unknown>('/api/v1/store/services-center', {
            params: {},
        });

export const getTimeSlots = (
    date: string,
    serviceCenterId: string,
    serviceTypeId: string
): Promise<AxiosResponse<unknown>> =>
    instance2
        .get<unknown>('/api/v1/appointments/time-slot', {
            params: {
                date: date,
                service_center_id: serviceCenterId,
                services_type_id: serviceTypeId,
            },
        });

export const bookSlot = (
    body: {
        inventory_id: string;
        product: string;
        service_type_id: string;
        service_center_id: string;
        date: string;
        time_slot_id: string;
        remarks: string;
    }
): Promise<AxiosResponse<unknown>> =>
    instance2
        .post<unknown>(
            '/api/v1/appointments',
            {
                inventory_id: body.inventory_id,
                product: body.product,
                service_type_id: body.service_type_id,
                service_center_id: body.service_center_id,
                date: body.date,
                time_slot_id: body.time_slot_id,
                remarks: body.remarks,
            },
            {}
        );
